import { useMemo, useState } from "react";
import { Check, Clock, Sparkles, Crown } from "lucide-react";
import { Reveal, PageHeader } from "@/components/Reveal";
import { PROCESS_STEPS } from "@/lib/content";
import { useContent } from "@/context/ContentContext";
import { useBooking } from "@/context/BookingContext";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function Treatments() {
  const { content } = useContent();
  const s = content?.settings || {};
  const showPrices = s.show_prices !== "off";
  const { book } = useBooking();
  const treatments = content?.treatments || [];
  const packages = content?.packages || [];
  const [tab, setTab] = useState("All Treatments");

  const categories = useMemo(
    () => ["All Treatments", ...new Set(treatments.map((t) => t.category))],
    [treatments]
  );
  const filtered = tab === "All Treatments" ? treatments : treatments.filter((t) => t.category === tab);

  return (
    <div className="px-4 pb-24 pt-44 sm:px-8 lg:px-16" data-testid="treatments-page">
      <PageHeader
        eyebrow="The Treatment Menu"
        title="Aesthetics, Elevated to Art"
        subtitle="Every protocol is physician-designed, clinically proven and tailored to your skin's unique story."
        testid="treatments-title"
      />

      <Reveal delay={0.25} className="mt-12 flex justify-center">
        <div className="glass flex max-w-full flex-wrap justify-center gap-1 rounded-full p-1.5" role="tablist" aria-label="Treatment categories">
          {categories.map((c) => (
            <button
              key={c}
              role="tab"
              aria-selected={tab === c}
              onClick={() => setTab(c)}
              data-testid={`treatment-tab-${slug(c)}`}
              className={`rounded-full px-4 py-2.5 text-xs font-medium tracking-wide transition-[background-color,color] duration-300 sm:px-5 ${
                tab === c ? "bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] text-[#1A1615]" : "text-[#5A504B] hover:text-[#B38B2D]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </Reveal>

      <div className="mx-auto mt-14 grid max-w-6xl gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="treatments-grid">
        {filtered.map((t, i) => (
          <Reveal key={t.id} delay={(i % 3) * 0.1}>
            <article
              className="glass glass-card-hover spotlight-card flex h-full flex-col overflow-hidden rounded-[2rem]"
              data-testid={`treatment-card-${t.id}`}
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
              }}
            >
              <div className="overflow-hidden">
                <img src={t.image} alt={t.name} loading="lazy" className="h-52 w-full object-cover transition-transform duration-700 ease-liquid hover:scale-105" />
              </div>
              <div className="flex flex-1 flex-col p-7">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-[#B38B2D]">{t.category}</p>
                  <div className="flex gap-2 text-[10px] font-medium text-[#91837C]">
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#F5EBE6] px-2.5 py-1">
                      <Clock className="h-3 w-3" aria-hidden="true" /> {t.duration}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#F5EBE6] px-2.5 py-1">
                      <Sparkles className="h-3 w-3" aria-hidden="true" /> {t.downtime}
                    </span>
                  </div>
                </div>
                <h3 className="mt-3 font-serif text-2xl font-semibold text-[#1A1615]">{t.name}</h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-[#5A504B]">{t.description}</p>
                <ul className="mt-4 space-y-2">
                  {(t.benefits || []).slice(0, 4).map((b) => (
                    <li key={b} className="flex items-center gap-2 text-xs text-[#5A504B]">
                      <Check className="h-3.5 w-3.5 shrink-0 text-[#B38B2D]" aria-hidden="true" /> {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex items-center justify-between border-t border-[#F0E6DD] pt-5">
                  {showPrices && <span className="font-serif text-2xl font-semibold text-[#B38B2D]">AED {t.price}</span>}
                  <button onClick={() => book(t.name)} data-testid={`treatment-book-${t.id}`} className="btn-gold px-5 py-2.5 text-xs">
                    Book Now
                  </button>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>

      {/* PROCESS */}
      <section className="mx-auto mt-24 max-w-6xl" data-testid="process-section">
        <div className="text-center">
          <Reveal><p className="eyebrow">The Experience</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-3 font-serif text-3xl font-medium text-[#1A1615] sm:text-4xl">Four Steps to Radiance</h2>
          </Reveal>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PROCESS_STEPS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.08}>
              <div className="glass glass-card-hover h-full rounded-[2rem] p-8" data-testid={`process-step-${p.n}`}>
                <p className="font-serif text-4xl italic text-[#D4AF37]/50">{p.n}</p>
                <h3 className="mt-4 font-serif text-xl font-semibold text-[#1A1615]">{p.title}</h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-[#5A504B]">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {packages.length > 0 && (content?.settings?.section_visibility?.treatments_packages ?? true) && (
        <section className="mx-auto mt-24 max-w-6xl" data-testid="packages-section">
          <Reveal>
            <div className="rounded-[3rem] bg-[#1A1615] px-6 py-16 sm:px-12 lg:px-16">
              <div className="mx-auto max-w-2xl text-center">
                <p className="eyebrow flex items-center justify-center gap-2 text-[#D4AF37]">
                  <Crown className="h-4 w-4" aria-hidden="true" /> VIP Skin Memberships
                </p>
                <h2 className="mt-3 font-serif text-3xl font-medium text-white sm:text-4xl">Radiance, on Repeat</h2>
                <p className="mt-4 text-sm font-light text-[#A39288]">
                  Members enjoy priority booking, exclusive pricing and a dedicated physician who knows their skin by heart.
                </p>
              </div>
              <div className="mt-12 grid gap-6 lg:grid-cols-3">
                {packages.map((p, i) => (
                  <Reveal key={p.id} delay={i * 0.12}>
                    <article
                      data-testid={`package-card-${p.id}`}
                      className={`relative flex h-full flex-col rounded-[2rem] border p-8 transition-[transform,box-shadow] duration-500 ease-liquid hover:-translate-y-1.5 ${
                        p.highlighted
                          ? "border-[#D4AF37] bg-gradient-to-b from-[#D4AF37]/15 to-transparent shadow-[0_0_40px_rgba(212,175,55,0.25)]"
                          : "border-white/10 bg-white/5"
                      }`}
                    >
                      {p.highlighted && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] px-4 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#1A1615]">
                          Most Loved
                        </span>
                      )}
                      <h3 className="font-serif text-2xl font-semibold text-white">{p.name}</h3>
                      <p className="mt-4">
                        {showPrices && <span className="font-serif text-4xl font-semibold text-[#F3E5AB]">{p.price}</span>}
                        <span className="ml-2 text-xs uppercase tracking-[0.15em] text-[#A39288]">{p.period}</span>
                      </p>
                      <ul className="mt-6 flex-1 space-y-3">
                        {(p.features || []).map((f) => (
                          <li key={f} className="flex items-start gap-2.5 text-sm font-light text-[#E8D5CE]">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" /> {f}
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => book()}
                        data-testid={`package-cta-${p.id}`}
                        className={`mt-8 w-full px-6 py-3.5 text-sm ${p.highlighted ? "btn-gold" : "btn-ghost-lux border-[#D4AF37]/40 text-[#F3E5AB] hover:bg-[#D4AF37]/10"}`}
                      >
                        {p.cta || "Join Now"}
                      </button>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          </Reveal>
        </section>
      )}
    </div>
  );
}
